/**
 * Interactive Municipality Map Component
 * 
 * Displays a choropleth map of Netherlands municipalities showing
 * average violations per facility using Leaflet.js
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Stack,
    Chip
} from '@mui/material';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix leaflet default marker icons issue with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Netherlands bounding box
const NETHERLANDS_BOUNDS = [
    [50.75, 3.2],  // Southwest
    [53.7, 7.2]    // Northeast
];

// URL to Dutch municipalities GeoJSON
// Using CBS (Statistics Netherlands) via cartomap.github.io - reliable mirror of official CBS data
// Alternative: You can also use https://cartomap.github.io/nl/wgs84/gemeente_2024.geojson
const DUTCH_MUNICIPALITIES_GEOJSON_URL = 'https://cartomap.github.io/nl/wgs84/gemeente_2024.geojson';

// Color scale for violations - based on percentage of facilities with violations
const getColorForViolations = (percentage) => {
    if (percentage === 0) return '#4caf50'; // Green - 0% with violations
    if (percentage <= 33) return '#8bc34a'; // Light green - low percentage
    if (percentage <= 66) return '#ff9800'; // Orange - medium percentage
    return '#f44336'; // Red - high percentage (>66%)
};

// Helper function to convert percentage to "1 op X" format
const formatPercentageAsRatio = (percentage) => {
    if (percentage === 0) return 'Geen';
    if (percentage === 100) return 'Alle';
    
    // Convert percentage to "1 op X" format
    const ratio = 100 / percentage;
    return `1 op ${ratio.toFixed(1)}`;
};

// Legend component - based on percentage of facilities with violations
const MapLegend = () => {
    const legendItems = [
        { label: 'Uitstekend', sublabel: '0% van de locaties', color: '#4caf50' },
        { label: 'Goed', sublabel: 'minder dan 33% van de locaties', color: '#8bc34a' },
        { label: 'Aandacht vereist', sublabel: '33% tot 66% van de locaties', color: '#ff9800' },
        { label: 'Zorgelijk', sublabel: 'meer dan 66% van de locaties', color: '#f44336' }
    ];

    return (
        <Paper
            sx={{
                position: 'absolute',
                bottom: 20,
                right: 20,
                zIndex: 1000,
                p: 2,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                minWidth: 240
            }}
        >
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Nalevingsniveau
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                aantal locaties met min. 1 overtreding
            </Typography>
            <Stack spacing={0.8}>
                {legendItems.map((item, index) => (
                    <Box key={index} display="flex" alignItems="flex-start" gap={1}>
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                bgcolor: item.color,
                                border: '1px solid #ccc',
                                borderRadius: 0.5,
                                flexShrink: 0,
                                mt: 0.2
                            }}
                        />
                        <Box>
                            <Typography variant="caption" fontWeight="bold" display="block">
                                {item.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {item.sublabel}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
};

// Component to fit map bounds
const FitBounds = ({ bounds }) => {
    const map = useMap();

    useEffect(() => {
        if (bounds && bounds.length > 0) {
            map.fitBounds(bounds);
        }
    }, [map, bounds]);

    return null;
};

const normalizeCityName = (city) => {
    if (!city) return 'Onbekend';
    const cleaned = city.trim();
    const lower = cleaned.toLowerCase();
    const replacements = {
        'den haag': 's-Gravenhage',
        'the hague': 's-Gravenhage',
        "'s-gravenhage": 's-Gravenhage'
    };
    for (const [oldValue, newValue] of Object.entries(replacements)) {
        if (lower.includes(oldValue)) {
            return newValue;
        }
    }
    return cleaned.replace(/\s+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase());
};

const normalizeCityKey = (city) => {
    if (!city) return '';
    const normalized = normalizeCityName(city);
    return normalized
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
};

// Main Municipality Map Component
const MunicipalityMap = ({ filters, loading: parentLoading, staticFacilities, staticMunicipalityStats }) => {
    const [municipalityData, setMunicipalityData] = useState(null);
    const [geoJsonData, setGeoJsonData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const selectedMetric = 'facilities_with_violations'; // Only one metric now


    // Available metrics configuration - simplified to only show violations
    const metrics = {
        facilities_with_violations: {
            label: 'Percentage Locaties met Overtredingen',
            description: 'Kaart gekleurd op basis van het percentage kinderopvanglocaties met minimaal 1 overtreding per gemeente',
            unit: '%',
            higher_is_worse: true,
            format: (val) => `${val}%`
        }
    };

    // Point-in-polygon check using ray-casting algorithm
    const isPointInPolygon = (point, polygon) => {
        const [lon, lat] = point;
        let inside = false;

        // Handle MultiPolygon by checking all polygons
        if (polygon.type === 'MultiPolygon') {
            for (const poly of polygon.coordinates) {
                if (isPointInPolygonCoords(lat, lon, poly[0])) {
                    return true;
                }
            }
            return false;
        }

        // Handle Polygon
        return isPointInPolygonCoords(lat, lon, polygon.coordinates[0]);
    };

    const isPointInPolygonCoords = (lat, lon, coords) => {
        let inside = false;
        for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
            const xi = coords[i][0], yi = coords[i][1];
            const xj = coords[j][0], yj = coords[j][1];

            const intersect = ((yi > lat) !== (yj > lat))
                && (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }
        return inside;
    };

    // Fetch facility coordinates and violations from backend (ignoring filters)
    const buildGeoJsonFromStats = async (stats) => {
        try {
            console.log('🗺️  Building municipality map from stats (no coordinate matching)...');
            const response = await fetch(DUTCH_MUNICIPALITIES_GEOJSON_URL);
            if (!response.ok) {
                throw new Error(`Failed to load geojson: ${response.statusText}`);
            }
            const data = await response.json();

            const statsMap = new Map();
            stats.forEach((entry) => {
                if (!entry || !entry.municipality) return;
                const name = normalizeCityName(entry.municipality);
                const key = normalizeCityKey(entry.municipality);
                const totalFacilities = entry.total_facilities || 0;
                const facilitiesWithViolations = entry.facilities_with_violations || 0;
                const percentage = entry.percentage_with_violations != null
                    ? entry.percentage_with_violations
                    : (totalFacilities > 0
                        ? Math.round((facilitiesWithViolations / totalFacilities) * 100)
                        : 0);
                statsMap.set(key, {
                    ...entry,
                    municipality: name,
                    percentage_with_violations: percentage,
                    facilities_0_violations: entry.facilities_0_violations ?? (totalFacilities - facilitiesWithViolations),
                    facilities_1_2_violations: entry.facilities_1_2_violations ?? 0,
                    facilities_3plus_violations: entry.facilities_3plus_violations ?? 0
                });
            });

            const enhancedFeatures = data.features.map((feature) => {
                const featureName = normalizeCityName(
                    feature.properties?.statnaam ||
                    feature.properties?.gemeentenaam ||
                    feature.properties?.name ||
                    feature.properties?.naam ||
                    'Onbekend'
                );
                const featureKey = normalizeCityKey(featureName);
                const munData = statsMap.get(featureKey) || null;
                return {
                    ...feature,
                    properties: {
                        ...feature.properties,
                        municipalityData: munData
                    }
                };
            });

            const munStats = Array.from(statsMap.values()).sort((a, b) => b[selectedMetric] - a[selectedMetric]);
            const overallStats = {
                total_municipalities: munStats.length,
                total_facilities: munStats.reduce((sum, m) => sum + (m.total_facilities || 0), 0),
                total_violations: munStats.reduce((sum, m) => sum + (m.total_violations || 0), 0),
                metrics: {}
            };
            const values = munStats.map(m => m[selectedMetric]).filter(v => v != null && !isNaN(v));
            if (values.length > 0) {
                overallStats.metrics[selectedMetric] = {
                    min: Math.min(...values),
                    max: Math.max(...values),
                    avg: values.reduce((sum, v) => sum + v, 0) / values.length
                };
            }

            setMunicipalityData({
                municipalities: munStats,
                statistics: overallStats
            });
            setGeoJsonData({
                type: 'FeatureCollection',
                features: enhancedFeatures
            });
        } catch (err) {
            console.error('Error building municipality stats map:', err);
            setError(`Fout bij ophalen van gemeentegrenzen: ${err.message}`);
        }
    };

    const fetchMunicipalityData = useCallback(async () => {
        console.log('🗺️  MunicipalityMap fetchMunicipalityData called');
        console.log('🗺️  staticFacilities received:', staticFacilities?.length || 0, staticFacilities);

        setLoading(true);
        setError(null);

        try {
            // Use precomputed municipality stats if available (production mode)
            if (staticMunicipalityStats && staticMunicipalityStats.length > 0) {
                console.log(`✅ Using static municipality stats: ${staticMunicipalityStats.length} municipalities`);
                await buildGeoJsonFromStats(staticMunicipalityStats);
                return;
            }

            // Use static facilities if available (production mode)
            if (staticFacilities && staticFacilities.length > 0) {
                console.log(`✅ Using static facility data: ${staticFacilities.length} facilities`);
                await fetchAndMatchMunicipalities(staticFacilities);
                return;
            }

            // Fallback to API (localhost only)
            if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
                console.log('⚠️ Map niet beschikbaar zonder data');
                setError('Kaart data niet beschikbaar');
                setLoading(false);
                return;
            }

            const apiBase = process.env.REACT_APP_API_BASE?.replace(/\/api$/, '') ||
                process.env.REACT_APP_API_URL ||
                'http://localhost:8010';

            console.log('📍 Fetching ALL facility coordinates and violations from API...');

            const response = await fetch(`${apiBase}/api/inspection-dashboard/analytics/municipality-map`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}) // Empty filters = all data
            });

            if (!response.ok) {
                throw new Error(`API Error: ${response.statusText}`);
            }

            const result = await response.json();

            if (result.success) {
                console.log(`✅ Loaded ${result.data.facilities.length} facilities with coordinates from API`);
                await fetchAndMatchMunicipalities(result.data.facilities);
            }
        } catch (err) {
            console.error('Error fetching facility data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [staticFacilities, staticMunicipalityStats]); // Add static data as dependency

    // Fetch municipality boundaries and match facilities using point-in-polygon
    const fetchAndMatchMunicipalities = async (facilities) => {
        try {
            console.log('🗺️  Fetching Dutch municipality boundaries from CBS data...');
            console.log(`📊 Processing ${facilities.length} facilities with coordinates`);

            // Fetch official Dutch municipality boundaries from CBS
            const response = await fetch(DUTCH_MUNICIPALITIES_GEOJSON_URL);

            if (!response.ok) {
                throw new Error(`CBS GeoJSON API error: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            console.log(`✅ Loaded ${data.features?.length || 0} municipality boundaries`);

            // Log first feature to see structure
            if (data.features && data.features.length > 0) {
                console.log('📋 Sample municipality properties:', Object.keys(data.features[0].properties));
            }

            // Match facilities to municipalities using point-in-polygon
            console.log('🔍 Matching facilities to municipalities...');

            const municipalityStats = new Map();
            let matchedCount = 0;
            let unmatchedCount = 0;

            facilities.forEach(facility => {
                const point = [facility.longitude, facility.latitude];
                let matched = false;

                // Check which municipality this facility falls within
                for (const feature of data.features) {
                    if (isPointInPolygon(point, feature.geometry)) {
                        // CBS data uses 'statnaam' for municipality name
                        const municipalityName = feature.properties?.statnaam ||
                            feature.properties?.gemeentenaam ||
                            feature.properties?.name ||
                            feature.properties?.naam ||
                            'Unknown';

                        // Aggregate statistics per municipality
                        if (!municipalityStats.has(municipalityName)) {
                            municipalityStats.set(municipalityName, {
                                municipality: municipalityName,
                                feature: feature,
                                facilities: [],
                                total_facilities: 0,
                                total_reports: 0,
                                total_violations: 0
                            });
                        }

                        const stats = municipalityStats.get(municipalityName);
                        stats.facilities.push(facility);
                        stats.total_facilities += 1;
                        stats.total_reports += facility.total_reports;
                        stats.total_violations += facility.total_violations;

                        matched = true;
                        matchedCount++;
                        break;
                    }
                }

                if (!matched) {
                    unmatchedCount++;
                    console.warn(`⚠️  Facility "${facility.facility_name}" at (${facility.latitude}, ${facility.longitude}) not matched to any municipality`);
                }
            });

            console.log(`✅ Matched ${matchedCount} facilities to municipalities`);
            if (unmatchedCount > 0) {
                console.log(`⚠️  ${unmatchedCount} facilities not matched (possibly outside NL or coordinates error)`);
            }

            // Calculate averages and build GeoJSON
            const enhancedFeatures = [];
            const munStats = [];

            municipalityStats.forEach((stats, munName) => {
                // Aggregate facility-level metrics
                const totalViolations = stats.facilities.reduce((sum, f) => sum + f.total_violations, 0);
                const totalApproved = stats.facilities.reduce((sum, f) => sum + f.approved_count, 0);
                const totalPartiallyApproved = stats.facilities.reduce((sum, f) => sum + f.partially_approved_count, 0);
                const totalRejected = stats.facilities.reduce((sum, f) => sum + f.rejected_count, 0);
                const totalFollowUp = stats.facilities.reduce((sum, f) => sum + f.follow_up_count, 0);

                // Count facilities by violation categories
                const facilities_0_violations = stats.facilities.filter(f => f.total_violations === 0).length;
                const facilities_1_2_violations = stats.facilities.filter(f => f.total_violations >= 1 && f.total_violations <= 2).length;
                const facilities_3plus_violations = stats.facilities.filter(f => f.total_violations >= 3).length;
                const facilities_with_violations = stats.facilities.filter(f => f.total_violations > 0).length;

                const numFacilities = stats.total_facilities;
                const numReports = stats.total_reports;

                // Calculate average violations per facility
                const avgViolationsPerFacility = Math.round((totalViolations / numFacilities) * 100) / 100;
                
                // Calculate percentage of facilities with violations (for coloring)
                const percentageWithViolations = Math.round((facilities_with_violations / numFacilities) * 100);

                // Calculate municipality-level metrics
                const munData = {
                    municipality: stats.municipality,
                    total_facilities: numFacilities,
                    total_reports: numReports,
                    total_violations: totalViolations,
                    // Violation categories
                    facilities_0_violations,
                    facilities_1_2_violations,
                    facilities_3plus_violations,
                    facilities_with_violations,
                    percentage_with_violations: percentageWithViolations,
                    // Per facility averages
                    avg_violations_per_facility: avgViolationsPerFacility,
                    // Rates
                    approval_rate: Math.round(((totalApproved + totalPartiallyApproved) / numReports * 100) * 10) / 10,
                    rejection_rate: Math.round((totalRejected / numReports * 100) * 10) / 10,
                    follow_up_rate: Math.round((totalFollowUp / numReports * 100) * 10) / 10
                };

                munStats.push(munData);

                enhancedFeatures.push({
                    ...stats.feature,
                    properties: {
                        ...stats.feature.properties,
                        municipalityData: munData
                    }
                });
            });

            // Sort by selected metric
            munStats.sort((a, b) => b[selectedMetric] - a[selectedMetric]);

            // Calculate overall statistics for each metric
            const overallStats = {
                total_municipalities: munStats.length,
                total_facilities: munStats.reduce((sum, m) => sum + m.total_facilities, 0),
                total_violations: munStats.reduce((sum, m) => sum + m.total_violations, 0),
                // Calculate min/max for all metrics
                metrics: {}
            };

            // For each metric, calculate min, max, and average
            Object.keys(metrics).forEach(metricKey => {
                const values = munStats.map(m => m[metricKey]).filter(v => v != null && !isNaN(v));
                if (values.length > 0) {
                    overallStats.metrics[metricKey] = {
                        min: Math.min(...values),
                        max: Math.max(...values),
                        avg: values.reduce((sum, v) => sum + v, 0) / values.length
                    };
                }
            });

            console.log('📊 Municipality statistics:', overallStats);

            setMunicipalityData({
                municipalities: munStats,
                statistics: overallStats
            });

            setGeoJsonData({
                type: 'FeatureCollection',
                features: enhancedFeatures
            });

        } catch (err) {
            console.error('Error fetching municipality boundaries:', err);
            setError(`Fout bij ophalen van gemeentegrenzen: ${err.message}`);
        }
    };

    // Load data on mount and when staticFacilities changes
    useEffect(() => {
        fetchMunicipalityData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staticFacilities, staticMunicipalityStats]); // Re-fetch when static data becomes available

    // Calculate bounds from GeoJSON
    const mapBounds = useMemo(() => {
        if (!geoJsonData || !geoJsonData.features.length) {
            return NETHERLANDS_BOUNDS;
        }

        const bounds = L.geoJSON(geoJsonData).getBounds();
        return [[bounds.getSouth(), bounds.getWest()], [bounds.getNorth(), bounds.getEast()]];
    }, [geoJsonData]);


    // Style function for GeoJSON features - based on percentage of facilities with violations
    const styleFeature = useCallback((feature) => {
        const munData = feature.properties.municipalityData;

        if (!munData) {
            return {
                fillColor: '#cccccc',
                weight: 1,
                opacity: 1,
                color: '#666',
                fillOpacity: 0.5
            };
        }

        // Use percentage of facilities with violations for coloring
        const percentage = munData.percentage_with_violations || 0;
        const color = getColorForViolations(percentage);

        return {
            fillColor: color,
            weight: 2,
            opacity: 1,
            color: '#fff',
            fillOpacity: 0.6  // Slightly more transparent
        };
    }, []);

    // Highlight feature on hover
    const highlightFeature = (e) => {
        const layer = e.target;
        const currentStyle = styleFeature(layer.feature);
        layer.setStyle({
            ...currentStyle,
            weight: 4,
            color: '#000',
            fillOpacity: 0.8  // Slightly more opaque on hover
        });
        layer.bringToFront();
    };

    // Reset highlight
    const resetHighlight = (e) => {
        const layer = e.target;
        const feature = layer.feature;
        const originalStyle = styleFeature(feature);
        layer.setStyle(originalStyle);
    };

    // Bind events to each feature
    const onEachFeature = (feature, layer) => {
        const munData = feature.properties.municipalityData;

        if (munData) {
            layer.bindTooltip(munData.municipality, {
                direction: 'top',
                opacity: 0.9,
                sticky: true
            });

            // Event listeners
            layer.on({
                mouseover: highlightFeature,
                mouseout: resetHighlight
            });
        }
    };

    if (loading || parentLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={600}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!municipalityData || !geoJsonData) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                Selecteer een of meerdere gemeenten uit het filter om de kaart te bekijken.
            </Alert>
        );
    }

    const currentMetric = metrics[selectedMetric];
    const currentMetricStats = municipalityData.statistics.metrics[selectedMetric];

    return (
        <Box>
            {/* Metric Label - No dropdown needed since there's only one metric */}
            <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                    {currentMetric.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {currentMetric.description}
                </Typography>
            </Box>

            {/* Statistics Summary */}
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <Chip
                    label={`Gemeenten: ${municipalityData.statistics.total_municipalities}`}
                    color="primary"
                    variant="outlined"
                />
                {currentMetricStats && (
                    <>
                        <Chip
                            label={`Gemiddeld: ${formatPercentageAsRatio(Math.round(currentMetricStats.avg * 10) / 10)} locaties`}
                            color="info"
                            variant="outlined"
                        />
                        <Chip
                            label={`Bereik: ${formatPercentageAsRatio(Math.round(currentMetricStats.min * 10) / 10)} - ${formatPercentageAsRatio(Math.round(currentMetricStats.max * 10) / 10)}`}
                            variant="outlined"
                        />
                    </>
                )}
            </Box>

            {/* Map Container */}
            <Card>
                <CardContent>
                    <Box position="relative" height={600}>
                        <MapContainer
                            center={[52.1, 5.3]}
                            zoom={7}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={true}
                            maxBounds={[
                                [50.5, 2.5],  // Southwest corner (beyond NL borders)
                                [54.0, 7.5]   // Northeast corner (beyond NL borders)
                            ]}
                            maxBoundsViscosity={1.0}
                            minZoom={7}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
                                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                            />

                            {geoJsonData && (
                                <GeoJSON
                                    key={selectedMetric}
                                    data={geoJsonData}
                                    style={styleFeature}
                                    onEachFeature={onEachFeature}
                                />
                            )}

                            <FitBounds bounds={mapBounds} />
                        </MapContainer>

                        {/* Legend */}
                        <MapLegend />
                    </Box>
                </CardContent>
            </Card>

        </Box>
    );
};

export default MunicipalityMap;

